import React, { useState, useEffect } from 'react';
import { offlineQueue, QueueStatus } from '../lib/offlineQueue';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { ScrollArea } from './ui/scroll-area';
import { 
  Cloud, 
  CloudOff, 
  RefreshCw, 
  Trash2, 
  AlertCircle, 
  Clock,
  Loader2,
  Check,
  Package,
  ArrowRightLeft,
  MessageSquare,
  ClipboardCheck
} from 'lucide-react';
import { format } from 'date-fns';

// Get icon for action type
const getActionIcon = (actionLabel) => {
  if (actionLabel === 'Restock' || actionLabel === 'Use/Consume') return Package;
  if (actionLabel === 'Transfer') return ArrowRightLeft;
  if (actionLabel === 'Send Message') return MessageSquare;
  if (actionLabel.includes('Adjustment') || actionLabel.includes('Approve') || actionLabel.includes('Reject')) return ClipboardCheck;
  return Package;
};

// Get summary from body
const getActionSummary = (item) => {
  const body = item.body;
  if (!body) return '';
  
  if (item.action_label === 'Restock' || item.action_label === 'Use/Consume') {
    return `Qty: ${body.qty || body.quantity || '?'}`;
  }
  if (item.action_label === 'Transfer') {
    return `From ${body.source_type || '?'} to ${body.destination_type || '?'}`;
  }
  return '';
};

export const PendingActionsDrawer = ({ trigger }) => {
  const [queueState, setQueueState] = useState({
    isOnline: navigator.onLine,
    isSyncing: false,
    lastSyncError: null,
    loginRequired: false,
    pendingCount: 0,
    failedCount: 0,
    totalCount: 0,
    items: [],
  });
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Subscribe to queue updates
    const unsubscribe = offlineQueue.subscribe(setQueueState);
    
    // Get initial state immediately
    offlineQueue.getQueueState().then(setQueueState);
    
    return unsubscribe;
  }, []);

  const handleRetryNow = async () => {
    await offlineQueue.retryNow();
  };

  const handleRemoveItem = async (id) => {
    await offlineQueue.removeItem(id);
  };

  const handleClearAll = async () => {
    await offlineQueue.clearAll();
    setClearAllDialogOpen(false);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case QueueStatus.PENDING:
        return <Badge variant="outline" className="text-xs"><Clock className="w-3 h-3 mr-1" /> Pending</Badge>;
      case QueueStatus.SYNCING:
        return <Badge variant="secondary" className="text-xs"><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Syncing</Badge>;
      case QueueStatus.FAILED:
        return <Badge variant="destructive" className="text-xs"><AlertCircle className="w-3 h-3 mr-1" /> Failed</Badge>;
      default:
        return null;
    }
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          {trigger}
        </SheetTrigger>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {queueState.isOnline ? (
                <Cloud className="w-5 h-5 text-green-500" />
              ) : (
                <CloudOff className="w-5 h-5 text-red-500" />
              )}
              Pending Actions
            </SheetTitle>
            <SheetDescription>
              {queueState.totalCount === 0 
                ? 'No pending actions to sync'
                : `${queueState.totalCount} action${queueState.totalCount !== 1 ? 's' : ''} waiting to sync`
              }
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            {/* Error/Login Required Banner */}
            {queueState.loginRequired && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Login required to sync pending actions</span>
                </div>
              </div>
            )}
            {queueState.lastSyncError && !queueState.loginRequired && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2 text-red-800">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Last sync failed: {queueState.lastSyncError}</span>
                </div>
              </div>
            )}
            
            {/* Status and controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 flex-wrap">
                {queueState.isSyncing && (
                  <Badge variant="secondary">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Syncing...
                  </Badge>
                )}
                {queueState.failedCount > 0 && (
                  <Badge variant="destructive">
                    {queueState.failedCount} failed
                  </Badge>
                )}
                {queueState.pendingCount > 0 && !queueState.isSyncing && (
                  <Badge variant="outline">
                    {queueState.pendingCount} pending
                  </Badge>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetryNow}
                  disabled={queueState.isSyncing || queueState.totalCount === 0 || !queueState.isOnline || queueState.loginRequired}
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${queueState.isSyncing ? 'animate-spin' : ''}`} />
                  Retry Now
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearAllDialogOpen(true)}
                  disabled={queueState.totalCount === 0}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Clear All
                </Button>
              </div>
            </div>

            {/* Queue items */}
            <ScrollArea className="h-[calc(100vh-250px)]">
              <div className="space-y-2 pr-4">
                {queueState.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Check className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>All synced!</p>
                    <p className="text-sm">Actions will appear here when offline</p>
                  </div>
                ) : (
                  queueState.items.map((item) => {
                    const ActionIcon = getActionIcon(item.action_label);
                    return (
                      <Card key={item.id} className={item.status === QueueStatus.FAILED ? 'border-destructive/50' : ''}>
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2 flex-1 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <ActionIcon className="w-4 h-4 text-primary" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{item.action_label}</span>
                                  {getStatusBadge(item.status)}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                  {getActionSummary(item)}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(item.created_at), 'MMM d, h:mm a')}
                                </p>
                                {item.last_error && (
                                  <p className="text-xs text-destructive mt-1 line-clamp-2">
                                    {item.last_error}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      {/* Clear All Confirmation Dialog */}
      <AlertDialog open={clearAllDialogOpen} onOpenChange={setClearAllDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all pending actions?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete all {queueState.totalCount} pending action{queueState.totalCount !== 1 ? 's' : ''}. 
              These actions will not be synced and any offline changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearAll} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Clear All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PendingActionsDrawer;
